/**
 * Cloud-backed RoleRepository. Registered explicitly at app start (see
 * `src/routes/__root.tsx`) so every admin surface reads and writes role
 * assignments in the database (`user_roles`) instead of browser storage.
 *
 * Registration must stay an explicit function call: the project is built with
 * `sideEffects: false`, so a side-effect-only import of this module is dropped
 * from the production bundle and the local repository would silently win.
 */
import {
  setRoleRepository,
  type Role,
  type RoleAssignment,
  type RoleRepository,
} from "./role-repository";
import {
  assignRoleFn,
  claimSuperAdminFn,
  getMyRolesFn,
  hasAnySuperAdminFn,
  listRoleAssignmentsFn,
  removeRoleFn,
} from "./console.functions";

const EVT = "cb16:roles-repository";

function emit() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(EVT));
}

function report(fn: string, e: unknown) {
  console.error(`[admin] ${fn} failed:`, e instanceof Error ? e.message : String(e));
}

class CloudRoleRepository implements RoleRepository {
  async getUserRoles(userId: string): Promise<Role[]> {
    if (!userId) return [];
    try {
      return ((await getMyRolesFn()) as string[]).filter(Boolean) as Role[];
    } catch (e) {
      report("getMyRolesFn", e);
      return [];
    }
  }
  async assignRole(userId: string, role: Role): Promise<void> {
    if (role === "super_admin") {
      const { exists } = await hasAnySuperAdminFn();
      if (!exists) {
        await claimSuperAdminFn();
        emit();
        return;
      }
    }
    await assignRoleFn({ data: { userId, role } });
    emit();
  }
  async removeRole(userId: string, role: Role): Promise<void> {
    await removeRoleFn({ data: { userId, role } });
    emit();
  }
  async listUsersWithRoles(): Promise<RoleAssignment[]> {
    const rows = await listRoleAssignmentsFn();
    return rows.map((r) => ({
      userId: r.userId,
      email: r.email,
      roles: r.roles as Role[],
      grantedAt: r.grantedAt,
    })) as RoleAssignment[];
  }
  async hasRole(userId: string, role: Role): Promise<boolean> {
    return (await this.getUserRoles(userId)).includes(role);
  }
  async hasAnySuperAdmin(): Promise<boolean> {
    try {
      return (await hasAnySuperAdminFn()).exists;
    } catch (e) {
      report("hasAnySuperAdminFn", e);
      return false;
    }
  }
  async reset(): Promise<void> {
    /* destructive reset is not available against the database */
  }
  subscribe(listener: () => void): () => void {
    if (typeof window === "undefined") return () => {};
    window.addEventListener(EVT, listener);
    return () => window.removeEventListener(EVT, listener);
  }
}

let installed = false;

export function installCloudRoleRepository() {
  if (installed) return;
  installed = true;
  setRoleRepository(new CloudRoleRepository());
}
